import styled, { css } from 'styled-components';

import { GhostButton } from 'ui/button';
import {
  borderRadius,
  colors,
  duration,
  easing,
  largeShadow,
  slideUpFade,
  spacing,
  subtleShadow,
  zIndex,
} from 'ui/theme';
import { body2, fontSizes, fonts, letterSpacing, overline } from 'ui/theme/typography';
import { pxToRem } from 'utils/convert';

import { OptionWrapperProps, SelectOptionsContainerProps, SelectWrapperProps } from './types';

export const BASIC_SELECT_VARIANTS = {
  ERROR: css`
    border-color: ${colors.borderDestructive};

    :hover {
      border-color: ${colors.borderDestructive};
    }

    :focus-within {
      border-color: ${colors.borderDestructive};
      box-shadow: 0 0 0 ${spacing.single} ${colors.borderDestructive};
    }
  `,
  FOCUS: css`
    :focus-within {
      border-color: ${colors.borderInteractive};
      box-shadow: 0 0 0 ${spacing.single} ${colors.borderInteractive};
      background: ${colors.backgroundInteractive};
      color: ${colors.textLink};

      path {
        fill: ${colors.iconInteractive};
      }
    }
  `,
  NON_INTERACTIVE: css`
    background: ${colors.backgroundSecondary};
    border-color: ${colors.borderPrimary};
    color: ${colors.textSecondary};

    :hover {
      background: ${colors.backgroundSecondary};
      color: ${colors.textSecondary};
      border-color: ${colors.borderPrimary};
      button {
        cursor: not-allowed;
      }
    }

    :focus-within {
      background: ${colors.backgroundSecondary};
      color: ${colors.textSecondary};
      border-color: ${colors.borderPrimary};
      box-shadow: none;
    }
  `,
  FULL_WIDTH: css`
    width: 100%;
  `,
};

export const CategoryGroup = styled.div`
  margin-bottom: ${spacing.quarter};
`;

export const CategoryHeader = styled.div<{ $showCategoryDivider?: boolean }>`
  align-items: center;
  display: flex;
  height: ${spacing.double};
  justify-content: space-between;
  padding-bottom: ${spacing.quarter};
  ${({ $showCategoryDivider }) =>
    $showCategoryDivider && `padding: ${spacing.threeEights} ${spacing.half};`}
`;

export const CategoryLabel = styled.span`
  ${overline}
  color: ${colors.grey50};
  text-transform: uppercase;
`;

export const OptionsListCategoryLabel = styled(CategoryLabel)`
  :first-child {
    width: 100%;
  }
`;

export const DoubleLine = styled.div<{ isSelected?: boolean }>`
  color: ${({ isSelected }) => (isSelected ? colors.textLink : colors.textSecondary)};
  font-size: ${fontSizes.s12};
  letter-spacing: ${letterSpacing.pos10};
  line-height: ${fontSizes.s16};
  overflow-wrap: break-word;
`;

export const GhostText = styled(GhostButton)`
  :focus {
    :before {
      opacity: 0;
    }
    text-decoration: underline;
  }
`;

export const OptionLabel = styled.div`
  overflow-wrap: break-word;
`;

export const OptionsContainer = styled.div<OptionWrapperProps>`
  background: ${({ isEmbedded }) => (isEmbedded ? 'none' : colors.backgroundPrimary)};
  box-shadow: ${({ isEmbedded }) => (isEmbedded ? 'none' : largeShadow.boxShadow)};
  border-radius: ${({ isEmbedded }) => (isEmbedded ? 0 : spacing.quarter)};
  min-width: ${({ width }) => width || spacing.x16};
  animation: ${slideUpFade} ${duration.short} ${easing.entrance};

  z-index: ${zIndex.dropdown};
  ${({ horizontalAlignment, verticalAlignment, triggerContainerHeight, isEmbedded }) => {
    if (isEmbedded) {
      return null;
    }
    const horizontalStyles = () => (horizontalAlignment === 'right' ? 'right: 0;' : 'left: 0;');
    const verticalSpacing = triggerContainerHeight
      ? pxToRem(triggerContainerHeight + 4)
      : spacing.x3;
    const verticalStyles = () =>
      verticalAlignment === 'up' ? `bottom: ${verticalSpacing};` : `top: ${verticalSpacing};`;
    return horizontalStyles() + verticalStyles();
  }}
  display: inline-block;
  position: ${({ isEmbedded }) => (isEmbedded ? 'relative' : 'absolute')};

  width: 100%;
`;

export const SelectOptionsContainer = styled.div<SelectOptionsContainerProps>`
  background: ${({ isEmbedded }) => (isEmbedded ? 'none' : colors.backgroundPrimary)};
  box-shadow: ${({ isEmbedded }) => (isEmbedded ? 'none' : largeShadow.boxShadow)};
  border-radius: ${({ isEmbedded }) => (isEmbedded ? 0 : borderRadius.lg)};
  margin-top: ${spacing.quarter};
  z-index: ${zIndex.newDropdown};
  animation: ${slideUpFade} ${duration.short} ${easing.entrance};
  display: inline-block;
  width: ${({ $triggerWidth, isFullWidth, width }) => {
    if (isFullWidth && $triggerWidth) {
      return pxToRem($triggerWidth);
    }
    return width || spacing.x16;
  }};
  font-family: ${fonts.sansSerif};
`;

export const OptionWrapper = styled.ul<OptionWrapperProps>`
  padding: ${({ isVirtualized }) => (isVirtualized ? '0px' : spacing.threeEights)};
  overflow-y: auto;
  overflow-x: hidden;
  max-height: ${pxToRem(280)};

  > * + * {
    margin-top: ${spacing.eighth};
  }

  /* this is gross, but we can't pass styles directly to the input wrapper
  on an input text when used in the filterable versions */
  [data-test='input-wrapper'] {
    margin-bottom: ${pxToRem(14)};
  }

  /* styling for full width selects */
  ${({ isFullWidth }) => isFullWidth && BASIC_SELECT_VARIANTS.FULL_WIDTH}
`;

export const SelectWrapper = styled.div<SelectWrapperProps>`
  background: ${colors.backgroundPrimary};
  border: solid ${spacing.single} ${colors.borderPrimary};
  box-shadow: ${subtleShadow.boxShadow};
  border-radius: ${borderRadius.md};
  transition: all ${duration.short} ${easing.entrance};
  flex: 1 1 auto;
  display: flex;

  path {
    transition: all ${duration.short} ${easing.entrance};
    fill: ${colors.iconPrimary};
  }

  [data-test='selected-option-empty'] {
    color: ${colors.textSecondary};
  }

  :hover {
    border-color: ${colors.borderInteractive};
    background: ${colors.backgroundInteractive};
    color: ${colors.textInteractiveHover};
    transition: all ${duration.short} ${easing.exit};

    path {
      transition: all ${duration.short} ${easing.exit};
      fill: ${colors.iconInteractiveHover};
    }

    [data-test='selected-option-empty'] {
      color: ${colors.textLink};
    }
  }

  /* order is important here */
  ${BASIC_SELECT_VARIANTS.FOCUS}
  ${({ isError }) => isError && BASIC_SELECT_VARIANTS.ERROR}
  ${({ isNonInteractive }) => isNonInteractive && BASIC_SELECT_VARIANTS.NON_INTERACTIVE}
  ${({ isFullWidth }) => isFullWidth && BASIC_SELECT_VARIANTS.FULL_WIDTH}
`;

export const ClearButton = styled(GhostText)`
  && {
    color: ${colors.textSecondary};
    flex: 0 0 auto;
    margin-right: ${spacing.threeQuarter};
  }
`;

export const TriggerContainer = styled.div`
  align-items: center;
  display: flex;
`;

export const SelectContainer = styled.div<{ isFullWidth?: boolean }>`
  ${body2}
  position: relative;
  display: inline-block;

  ${({ isFullWidth }) => isFullWidth && BASIC_SELECT_VARIANTS.FULL_WIDTH}
`;

export const SelectDropdownInfoText = styled.div`
  color: ${colors.textSecondary};
`;

export const FooterButton = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: ${spacing.half};
  border-top: solid ${spacing.single} ${colors.borderPrimarySubtle};
`;

export const StickyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: -${spacing.threeEights};
  margin: -${spacing.threeEights} -${spacing.threeEights} ${spacing.threeEights};
  background-color: ${colors.backgroundPrimary};
  padding: ${spacing.threeEights};
  border-radius: ${spacing.threeEights} ${spacing.threeEights} 0 0;
  border-bottom: ${spacing.single} solid ${colors.borderPrimarySubtle};
  gap: ${spacing.threeEights};
  z-index: 2;
`;

export const CategorySeparator = styled.hr`
  all: unset;
  display: block;
  width: 100%;
  height: 0;
  padding: 0;
  margin: ${spacing.half} 0;
  border-bottom: 1px solid ${colors.borderPrimarySubtle};
`;
